import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {BioentityInfo} from 'folder-items/FileConstants';
import {ClientId, FromServerId, ToServerId} from 'notebook/ELNConstants';
import {extractContainerSchemaAndColumnGroup} from 'notebook/integrations/samples/TransferStructuredTableSchemasUtil';
import addSamplesFromStructuredTable from 'notebook/sample-propagation/addSamplesFromStructuredTable';
import addSamplesFromWorklist from 'notebook/sample-propagation/addSamplesFromWorklist';
import {addToWorklistActionFunction} from 'notebook/structured-tables/action-functions/addToWorklistActionFunction';
import {createDatasetWithPlotsActionFunction} from 'notebook/structured-tables/action-functions/createDatasetWithPlotsActionFunction';
import {exportAuditLogsActionFunction} from 'notebook/structured-tables/action-functions/exportAuditLogsActionFunction';
import {printBarcodesActionFunction} from 'notebook/structured-tables/action-functions/printBarcodesActionFunction';
import {submitLookupTableActionFunction} from 'notebook/structured-tables/action-functions/submitLookupTableActionFunction';
import {isContainersStructuredTableColumn} from 'notebook/structured-tables/columns/TypePredicates';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import LookupStructuredTableRowUpdater from 'notebook/structured-tables/row-updaters/LookupStructuredTableRowUpdater';
import {getSpreadsheetTypeForColumn} from 'notebook/structured-tables/spreadsheet-import/getSpreadsheetColumnTypes';
import importRowsFromSpreadsheet from 'notebook/structured-tables/spreadsheet-import/importRowsFromSpreadsheet';
import {
  ActionFunctions,
  BaseStructuredTable,
  PrimarySample,
  PrimarySampleLookupType,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableAddFromSourceType,
  StructuredTableAddOperation,
  StructuredTableColumn,
  StructuredTableColumnSummary,
  StructuredTableKind,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import getColumnSummaryForSchema from 'notebook/structured-tables/util/getColumnSummaryForSchema';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {
  getBioentityInfoFromTypedValue,
  getStorableInfoFromTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {StorableInfo} from 'notebook/tables/typed-values/TypedValueTypes';
import {createProcessActionFunction} from 'processes/tables/action-functions/createProcessActionFunction';
import {createRequestV2SubmissionActionFunction} from 'processes/tables/action-functions/createRequestV2SubmissionActionFunction';
import {BoxSchema, ContainerSchema, PlateSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import APIUtil from 'util/APIUtil';
import {WORKLIST_FILL_LOCATION} from 'worklists/WorklistConstants';

import {getTagSchemaForStructuredTable} from '../registry/StructuredTableUtilsForRegistration';

export enum LookupTableInsertType {
  EXISTING_BIOENTITIES,
  INVENTORY,
}

/**
 * Keep in sync with benchling/notebook/structured_tables/constants/lookup_tables.py
 */
export interface LookupStructuredTableMetadata {}

/**
 * isFinalized for Lookup Tables behaves differently than most. For Lookup Tables, this value essentially only
 * represents that a given row was used for Table Mapping. It does not have any effect in other contexts.
 */
export interface LookupStructuredTableRowMetadata extends RowMetadata {}

export type GenericLookupStructuredTable<TIds> = BaseStructuredTable<
  LookupStructuredTableMetadata,
  LookupStructuredTableRowMetadata,
  TIds
>;

export type LookupStructuredTable = GenericLookupStructuredTable<ClientId>;
export type FromServerLookupStructuredTable = GenericLookupStructuredTable<FromServerId>;
export type ToServerLookupStructuredTable = GenericLookupStructuredTable<ToServerId>;

export default class LookupStructuredTableAdapter extends GenericStructuredTableAdapter<
  LookupStructuredTableMetadata,
  LookupStructuredTableRowMetadata
> {
  getDisplayName(structuredTable: LookupStructuredTable): string {
    return 'Lookup';
  }

  getBlankRowMetadata(tableMetadata: LookupStructuredTableMetadata): LookupStructuredTableRowMetadata {
    return {};
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: LookupStructuredTableMetadata;
  }): StructuredTableColumn {
    const containerSchema = extractContainerSchemaAndColumnGroup(schemas);
    if (!containerSchema) {
      return column;
    }
    // toContainersColumnDefinition uses the schema stored in the frontend metadata to assign a schema for
    // the column, which is used in narrowing lookup config schema options
    if (isContainersStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {
          containerSchemaId: containerSchema.id,
          containerSchemaName: containerSchema.name,
        },
      };
    }

    return column;
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: LookupStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const schemaId = schemas[0].id;
    let columnType;
    if (APIUtil.isTagSchema(schemaId) || APIUtil.isSchemaInterface(schemaId)) {
      columnType = TableColumnType.EXISTING_BIOENTITY;
    } else if (APIUtil.isBoxSchema(schemaId)) {
      columnType = TableColumnType.BOX;
    } else if (APIUtil.isContainerSchema(schemaId)) {
      columnType = TableColumnType.CONTAINERS;
    } else if (APIUtil.isPlateSchema(schemaId)) {
      columnType = TableColumnType.PLATE;
    }

    return columnType
      ? [
          makeStructuredTableColumn({
            columnType,
            name: schemas[0].name,
            isRequired: true,
          }),
        ]
      : [];
  }

  override getActionFunctions(
    tableMetadata: LookupStructuredTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<LookupStructuredTableMetadata, {}> {
    const inherited = super.getActionFunctions(tableMetadata, schemas);
    return {
      primary: BenchlingConfig.ELN_ENABLE_LOOKUP_TABLE_MAPPING ? [submitLookupTableActionFunction] : [],
      secondary: [
        ...(inherited.secondary ?? []),
        createRequestV2SubmissionActionFunction,
        createProcessActionFunction,
        addToWorklistActionFunction,
        createDatasetWithPlotsActionFunction,
      ],
      // lookup tables cannot be submitted, so remove the option to export audit logs
      overflow: [
        ...(inherited.overflow ?? []).filter((action) => action !== exportAuditLogsActionFunction),
        printBarcodesActionFunction,
      ],
      footer: inherited.footer ?? [],
    };
  }
  override getAddFromSourceOperations(
    entryId: string,
    structuredTable?: LookupStructuredTable
  ): Array<StructuredTableAddOperation<LookupStructuredTableMetadata, LookupStructuredTableRowMetadata>> {
    const ops: Array<
      StructuredTableAddOperation<LookupStructuredTableMetadata, LookupStructuredTableRowMetadata>
    > = [
      {
        type: StructuredTableAddFromSourceType.ADD_FROM_TABLE,
        onSelect: ({note, structuredTable, addSamples}) => {
          addSamplesFromStructuredTable({
            destinationTable: structuredTable,
            adapter: this,
            note,
            entryId,
            addSamples,
          });
        },
      },
    ];
    if (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_FROM_WORKLIST,
        onSelect: ({structuredTable, addSamples}) => {
          addSamplesFromWorklist(structuredTable, this, addSamples, WORKLIST_FILL_LOCATION.LOOKUP_TABLE);
        },
      });
    }

    // Spreadsheet import
    ops.push({
      type: StructuredTableAddFromSourceType.ADD_FROM_SPREADSHEET,
      onSelect: async ({structuredTable, addFullRows}) => {
        const schemaId = structuredTable.schemas[0].id;
        let schemasInput;
        let schemaType: string | null;
        if (APIUtil.isTagSchema(schemaId)) {
          schemasInput = {tagSchema: getTagSchemaForStructuredTable(structuredTable) as TagSchema};
          schemaType = ModelTypeConstants.TAG_SCHEMA;
        } else if (APIUtil.isSchemaInterface(schemaId)) {
          schemasInput = {schemaInterface: structuredTable.schemas[0]};
          schemaType = ModelTypeConstants.SCHEMA_INTERFACE;
        } else if (APIUtil.isBoxSchema(schemaId)) {
          schemasInput = {boxSchema: structuredTable.schemas[0] as BoxSchema};
          schemaType = ModelTypeConstants.BOX;
        } else if (APIUtil.isContainerSchema(schemaId)) {
          schemasInput = {containerSchema: structuredTable.schemas[0] as ContainerSchema};
          schemaType = ModelTypeConstants.CONTAINER;
        } else if (APIUtil.isPlateSchema(schemaId)) {
          schemasInput = {plateSchema: structuredTable.schemas[0] as PlateSchema};
          schemaType = ModelTypeConstants.PLATE;
        } else {
          assert(false, `Invalid schema for lookup table: ${structuredTable.schemas[0]}`);
        }
        const rows = await importRowsFromSpreadsheet({
          structuredTable,
          schemas: schemasInput,
          getSpreadsheetTypeForColumn: (column) =>
            getSpreadsheetTypeForColumn(schemaType, column, StructuredTableKind.LOOKUP),
        });

        if (rows) {
          addFullRows(rows);
        }
      },
    });

    return ops;
  }

  override getTableKindRowUpdater(): typeof LookupStructuredTableRowUpdater {
    return LookupStructuredTableRowUpdater;
  }

  // Keep in sync with LookupTableInstantiator.get_primary_sample_ids_for_row in
  // benchling/notebook/structured_tables/instantiators/lookup_table_instantiator.py (tested by
  // TestLookupInstantiatorPrimarySamples in
  // tests/unit/notebook/structured_tables/instantiators/lookup_table_instantiator_test.py)
  override getPrimarySamples(
    structuredTable: StructuredTable<LookupStructuredTableMetadata, LookupStructuredTableRowMetadata>,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[rowId: string]: Array<PrimarySample>} {
    const primarySamplesByRowId: {[rowId: string]: Array<PrimarySample>} = {};
    const schema = structuredTable.schemas[0];

    if (schema == null) {
      // Most likely, the user doesn't have permissions on the schema
      for (const {rowId} of structuredTable.rows) {
        primarySamplesByRowId[rowId] = [];
      }
      return primarySamplesByRowId;
    }

    const columnIndex = structuredTable.columns.findIndex(
      ({columnType}) => columnType !== TableColumnType.DEDICATED_LOOKUP
    );
    assert(columnIndex !== -1, 'Expected structured table to have a primary column ');

    structuredTable.rows.forEach(({rowId, cells}, rowIndex) => {
      const typedValue = computedTable?.[rowIndex][columnIndex]?.value ?? cells[columnIndex].typedValue;
      let sample: BioentityInfo | StorableInfo | null;
      if (structuredTable.columns[columnIndex].columnType === TableColumnType.EXISTING_BIOENTITY) {
        sample = getBioentityInfoFromTypedValue(typedValue);
      } else {
        sample = typedValue ? getStorableInfoFromTypedValue(typedValue) : null;
      }

      primarySamplesByRowId[rowId] =
        sample != null
          ? [
              {
                schemaId: schema.id,
                columnGroup: null,
                sampleLookupInfo: {sampleType: PrimarySampleLookupType.DIRECT, idForLookup: sample.id},
              },
            ]
          : [];
    });
    return primarySamplesByRowId;
  }

  override getColumnSummariesForCreateProcessShortcut(
    structuredTable: StructuredTable<LookupStructuredTableMetadata, LookupStructuredTableRowMetadata>
  ): Array<StructuredTableColumnSummary & {columnIndex: number}> {
    const columnIndex = structuredTable.columns.findIndex(
      (column) =>
        column.columnType !== TableColumnType.DEDICATED_LOOKUP && column.columnType !== TableColumnType.CUSTOM
    );
    assert(columnIndex !== -1, 'Expected structured table to have a primary column ');
    assert(structuredTable.schemas.length === 1, 'Expected lookup table to have exactly one schema');
    return [
      {
        ...getColumnSummaryForSchema(structuredTable.schemas[0]),
        columnIndex,
      },
    ];
  }

  // Lookup Tables don't ever lock their searching columns
  override getNonFinalizingColumnTypes(): Array<TableColumnType> {
    return [
      ...super.getNonFinalizingColumnTypes(),
      TableColumnType.EXISTING_BIOENTITY,
      TableColumnType.CONTAINERS,
      TableColumnType.PLATE,
      TableColumnType.BOX,
    ];
  }

  override enableCustomColumns(structuredTable: LookupStructuredTable): boolean {
    return true;
  }

  override enableLookupColumns(structuredTable: LookupStructuredTable): boolean {
    return true;
  }

  // Keep in sync with LookupTableInstantiator.allow_delete_table in
  // benchling/notebook/structured_tables/instantiators/lookup_table_instantiator.py
  override allowDeleteTable(structuredTable: LookupStructuredTable): {
    canDelete: boolean;
    reason?: string;
  } {
    // Lookup table "isFinalized" indicates table-mapping state, not submission state.
    // Always allow deleting lookup tables, and don't call super here.
    return {canDelete: true};
  }
}
